// Download helpers — pick the right resource for an asset, derive a sensible
// filename, and trigger an authenticated "Save as". Used by the asset detail
// panel's Download button.

import { api, downloadAuthedFile } from '../api/client';
import type { AssetResourceDTO } from '../api/types';
import { exceedsZip32, zipStore, type ZipEntry } from './zip';

/// UTI → file extension. Covers what the corpus actually stores (iPhone HEIC/
/// HEVC, transcoded JPEG/MP4, plus common web formats). Unknown → "bin".
const UTI_EXT: Record<string, string> = {
  'public.jpeg': 'jpg',
  'public.png': 'png',
  'public.heic': 'heic',
  'public.heif': 'heic',
  'public.heif-standard': 'heic',
  'public.tiff': 'tiff',
  'com.compuserve.gif': 'gif',
  'public.mpeg-4': 'mp4',
  'public.hevc': 'mov',
  'com.apple.quicktime-movie': 'mov',
  'public.movie': 'mov',
};

export function extForUTI(uti: string): string {
  return UTI_EXT[uti.toLowerCase()] ?? 'bin';
}

/// Minimal shape we need off an asset (works for both AssetDTO and
/// AssetDetailDTO without coupling to either).
interface DownloadableAsset {
  id: string;
  caption?: string | null;
  primary_resource_id?: string | null;
  resources: AssetResourceDTO[];
}

/// Pick the resource to download: the original primary if present, then any
/// non-derivative resource, then whatever's first. We deliberately prefer the
/// ORIGINAL over server-made `alternate_*` derivatives so "download" yields
/// the real file the user uploaded.
export function pickDownloadResource(
  asset: DownloadableAsset,
): AssetResourceDTO | null {
  const rs = asset.resources ?? [];
  if (rs.length === 0) return null;
  const primary = rs.find((r) => r.id === asset.primary_resource_id);
  if (primary) return primary;
  const original = rs.find((r) => !r.type.startsWith('alternate_'));
  return original ?? rs[0]!;
}

function sanitize(name: string): string {
  const cleaned = name
    .trim()
    .replace(/[^\w.-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
  return cleaned.length > 0 ? cleaned : 'photo';
}

export function downloadFilename(
  asset: DownloadableAsset,
  resource: AssetResourceDTO,
): string {
  const base = asset.caption && asset.caption.trim().length > 0
    ? sanitize(asset.caption)
    : `dvphoto-${asset.id.slice(0, 8)}`;
  return `${base}.${extForUTI(resource.uti)}`;
}

/// Download the asset's original (primary) resource via an authed fetch.
export async function downloadAsset(asset: DownloadableAsset): Promise<void> {
  const resource = pickDownloadResource(asset);
  if (!resource) throw new Error('No downloadable resource for this asset.');
  const path = `/v1/assets/${asset.id}/resources/${resource.id}`;
  await downloadAuthedFile(path, downloadFilename(asset, resource));
}

/// Ensure a filename is unique within a set, appending -1/-2/… before the
/// extension on collision.
function dedupeName(name: string, seen: Map<string, number>): string {
  const count = seen.get(name);
  if (count === undefined) {
    seen.set(name, 0);
    return name;
  }
  const next = count + 1;
  seen.set(name, next);
  const dot = name.lastIndexOf('.');
  return dot > 0
    ? `${name.slice(0, dot)}-${next}${name.slice(dot)}`
    : `${name}-${next}`;
}

/// Download several assets bundled into a single .zip (STORE method — no
/// recompression of already-compressed media). Fetches each original
/// sequentially (one in memory at a time during fetch), builds the archive,
/// and saves it. For batches that would overflow ZIP's 32-bit fields
/// (~4 GiB) this falls back to per-file downloads. Returns the tally; never
/// throws on individual failures.
export async function downloadAssetsZip(
  assets: DownloadableAsset[],
  zipName: string,
  onProgress?: (done: number, total: number) => void,
): Promise<{ ok: number; failed: number; zipped: boolean }> {
  const entries: ZipEntry[] = [];
  const seen = new Map<string, number>();
  let ok = 0;
  let failed = 0;

  for (let i = 0; i < assets.length; i++) {
    const asset = assets[i]!;
    const resource = pickDownloadResource(asset);
    if (!resource) {
      failed++;
      onProgress?.(i + 1, assets.length);
      continue;
    }
    try {
      const resp = await api.rawGet(
        `/v1/assets/${asset.id}/resources/${resource.id}`,
        '*/*',
      );
      const data = new Uint8Array(await resp.arrayBuffer());
      entries.push({ name: dedupeName(downloadFilename(asset, resource), seen), data });
      ok++;
    } catch {
      failed++;
    }
    onProgress?.(i + 1, assets.length);
  }

  if (entries.length === 0) return { ok, failed, zipped: false };

  // Too big for a 32-bit zip — save the originals individually instead.
  if (exceedsZip32(entries.map((e) => ({ size: e.data.length })))) {
    for (const e of entries) {
      const url = URL.createObjectURL(
        new Blob([e.data] as unknown as BlobPart[]),
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = e.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      await new Promise((r) => setTimeout(r, 300));
    }
    return { ok, failed, zipped: false };
  }

  const blob = zipStore(entries);
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = zipName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return { ok, failed, zipped: true };
}

/// Human-readable byte size for the download button hint.
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}
